'use client';

import { format } from 'date-fns';
import FuzzySearch from 'fuzzy-search';
import Link from 'next/link';
import React, { useEffect, useState } from 'react';

import { truncate } from '@/lib/utils';
import { Article } from '@/types/types';

export const BlogPostRows = ({ articles }: { articles: Article[] }) => {
  const [search, setSearch] = useState('');

  const searcher = new FuzzySearch(articles, ['title'], {
    caseSensitive: false,
  });

  const [results, setResults] = useState(articles);
  useEffect(() => {
    const results = searcher.search(search);
    setResults(results);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  return (
    <div className="w-full py-20">
      <div className="flex sm:flex-row flex-col justify-between gap-4 items-center mb-12">
        <p className="text-3xl font-extrabold tracking-tight text-neutral-900 dark:text-white">
          More Posts
        </p>
        <div className="relative w-full sm:w-auto">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search articles..."
            className="text-sm min-w-full sm:min-w-96 px-4 py-3 rounded-xl bg-neutral-100 dark:bg-neutral-900 border border-transparent focus:border-neutral-300 dark:focus:border-neutral-700 focus:ring-4 focus:ring-neutral-200/50 dark:focus:ring-neutral-800/50 focus:outline-none transition-all text-neutral-900 dark:text-neutral-200 placeholder-neutral-500 dark:placeholder-neutral-500 shadow-sm"
          />
        </div>
      </div>

      <div className="divide-y divide-neutral-200 dark:divide-neutral-800 border-t border-b border-neutral-200 dark:border-neutral-800">
        {results.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <p className="text-neutral-500 dark:text-neutral-400 font-medium text-lg">No articles found matching "{search}"</p>
            <p className="text-neutral-400 dark:text-neutral-500 text-sm mt-2">Try adjusting your search terms.</p>
          </div>
        ) : (
          results.map((article, index) => (
            <BlogPostRow article={article} key={article.slug + index} />
          ))
        )}
      </div>
    </div>
  );
};

export const BlogPostRow = ({ article }: { article: Article }) => {
  return (
    <Link
      href={`blog/${article.slug}`}
      key={`${article.slug}`}
      className="flex md:flex-row flex-col items-start justify-between md:items-center group py-8 hover:bg-neutral-50 dark:hover:bg-neutral-900/50 transition-colors duration-300 px-4 -mx-4 rounded-2xl"
    >
      <div className="flex-1">
        <h3 className="text-neutral-900 dark:text-neutral-100 text-xl font-bold group-hover:text-primary dark:group-hover:text-primary transition duration-200 leading-snug">
          {article.title}
        </h3>
        <p className="text-neutral-600 dark:text-neutral-400 text-base mt-3 max-w-2xl leading-relaxed">
          {truncate(article.description, 120)}
        </p>

        <div className="flex gap-4 items-center mt-6 flex-wrap">
          <time
            dateTime={article.publishedAt}
            className="text-neutral-500 dark:text-neutral-500 text-sm font-medium"
          >
            {format(new Date(article.publishedAt), 'MMMM dd, yyyy')}
          </time>
          {article.categories && article.categories.length > 0 && (
            <>
              <div className="h-1 w-1 rounded-full bg-neutral-300 dark:bg-neutral-700 hidden sm:block"></div>
              <div className="flex gap-2 flex-wrap">
                {article.categories?.map((category, idx) => (
                  <span
                    key={`category-${idx}`}
                    className="text-xs font-semibold px-3 py-1 rounded-full bg-neutral-100 text-neutral-700 dark:bg-neutral-800 dark:text-neutral-300 capitalize tracking-wide border border-neutral-200 dark:border-neutral-700/50"
                  >
                    {category.name}
                  </span>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </Link>
  );
};
